import { isEqual } from 'lodash';
import { fromJS } from 'immutable';

import { add, set, update, clear, clip } from 'pluginServices/selectionState/actionCreators';
import { SELECTION_STATE, COURSEWARE_ITEMS, SUB_TARGET } from 'pluginServices/selectionState/constants';
import { dispatch, getState } from 'pluginServices/store';
import CoursewareTargetProvider from 'pluginServices/services/CoursewareTargetService';

import { pushToast } from 'containers/ToastManager/actions';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import messages from 'containers/Clipboard/messages';

class SelectionService {
  constructor({ target }) {
    this._target = target;
    this.subscribers = [];
    this.targetProvider = CoursewareTargetProvider.build();
  }

  async _createCoursewareTarget(elementId) {
    const { type, ancestry, id } = await this.targetProvider.createTarget(elementId);
    const courseware = {
      id,
      type,
      ancestry,
    };
    return courseware;
  }

  get() {
    return getState().getIn([SELECTION_STATE, COURSEWARE_ITEMS]).toJS();
  }

  set(subTarget) {
    let courseware = fromJS(this._target);
    if (subTarget) {
      courseware = courseware.set(SUB_TARGET, subTarget);
    }
    dispatch(set(courseware));
  }

  add(subTarget) {
    const coursewareId = this._target.id;
    let courseware = fromJS(this._target);
    if (subTarget) {
      courseware = courseware.set(SUB_TARGET, subTarget);
    }
    const coursewareItems = this.get().filter((coursewareItem) => coursewareItem.id === coursewareId);
    const coursewareSubTargetExists = coursewareItems
      .find((coursewareItem) => isEqual(subTarget, coursewareItem.subTarget));
    if (!coursewareSubTargetExists) {
      dispatch(add(courseware));
    }
  }

  _subscribe = (listener) => {
    this.subscribers.push(listener);
    const coursewareItems = fromJS(this.get());
    if (coursewareItems.size) {
      listener(coursewareItems.last().toJS());
    }
    return () => this._unsubscribe(listener);
  }

  _unsubscribe = (listener) => {
    if (this.subscribers.includes(listener)) {
      this.subscribers.splice(this.subscribers.indexOf(listener), 1);
    }
  }

  subscribeToSelection(callback) {
    this._subscribe(callback);
  }

  clear() {
    dispatch(clear());
  }

  async remove(subTarget) {
    const coursewareItems = this.get();
    const coursewareItemIndex = coursewareItems.findIndex((coursewareItem) => (
      coursewareItem.id === this._target.id &&
      isEqual(subTarget, coursewareItem.subTarget)
    ));

    if (coursewareItemIndex !== -1) {
      // removing current target from the selection
      coursewareItems.splice(coursewareItemIndex, 1);
      // check if parent is in the selection ancestry
      const parent = this._target.ancestry.length ? this._target.ancestry[0] : {};

      const isParentInSelection = coursewareItems.findIndex((coursewareItem) =>
        coursewareItem.id === parent.elementId) === -1;
      if (isParentInSelection) {
        const parentCourseware = await this._createCoursewareTarget(parent.elementId);
        coursewareItems.push(parentCourseware);
      }
    }

    dispatch(update(coursewareItems));
  }

  static build(...args) {
    return new SelectionService(...args);
  }

  pushToastMessage(message) {
    dispatch(pushToast({ message }));
  }

  alertCopySuccess() {
    this.pushToastMessage(intl.formatMessage(messages.stageElementSuccess));
  }

  paste(data) {
    try {
      const stage = JSON.parse(data).filter(({ type }) => type !== 'INTERACTIVE');
      const screen = this.get().find(({ type }) => type === 'INTERACTIVE');
      dispatch(clip([{ stage, screen }]));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }
}

export default SelectionService;
